import React, { useCallback, useEffect, useState } from 'react';

import Text from '../base/Text';
import CrossIcon from '../icons/CrossIcon';
import SpinIcon from '../icons/SpinIcon';
import Button from '../input/Button';

export type Notice = {
  id: number;
  message: string;
  timeout: number;
  type: 'info' | 'success' | 'warning' | 'error' | 'action';
  actionText?: string;
  onAction?: () => void;
  customIconName?: 'spinIcon' | 'noticeIcon';
  customBackgroundColor?: string;
  customTextColor?: string;
};

export default function Notice({
  id,
  timeout,
  type,
  message,
  actionText,
  onAction,
  customIconName,
  customBackgroundColor,
  customTextColor,
  removeCallback,
}: {
  removeCallback: () => void;
} & Readonly<Omit<Notice, 'customIcon'>>) {
  const duration = 300;
  const [hidden, setHidden] = useState(true);

  const close = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | undefined) => {
      if (e) {
        // Kill event bubbling in case
        // a modal is open to not trigger
        // onOpenChange
        e.preventDefault();
        e.stopPropagation();
      }
      setHidden(true);
      setTimeout(removeCallback, duration + 50);
    },
    [id],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const initTimeout = setTimeout(() => setHidden(false), 0);
    const closeTimeout = setTimeout(close, timeout);
    return () => {
      clearTimeout(initTimeout);
      clearTimeout(closeTimeout);
    };
  }, [close, timeout]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (type === 'action' && onAction) {
      e.preventDefault();
      e.stopPropagation();
      onAction();
      close(e);
    } else {
      close(e);
    }
  };
  const bgColor =
    customBackgroundColor ||
    (type === 'error'
      ? 'bg-red/30'
      : type === 'info' || type === 'warning'
        ? 'bg-blue/30'
        : 'bg-green-50/30');

  const textColor =
    customTextColor ||
    (type === 'error'
      ? 'text-red'
      : type === 'info' || type === 'warning'
        ? 'text-blue'
        : 'text-green-50');

  const iconToShow =
    customIconName === undefined || customIconName === 'noticeIcon' ? (
      <NoticeIcon className={textColor} type={type} />
    ) : (
      <SpinIcon />
    );

  return (
    <div
      className={`shadow-menu text-gray-90 pointer-events-auto mx-auto mt-4 flex min-h-12 w-fit max-w-lg cursor-pointer items-center gap-3 overflow-hidden rounded-2xl bg-white p-4 pr-6 transition duration-300 ease-in-out ${
        hidden ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
      }`}
      onClick={handleClick}
      data-state
    >
      <div
        className={`flex h-8 w-8 flex-none items-center justify-center self-start rounded-lg ${
          bgColor
        }`}
      >
        {iconToShow}
      </div>
      <Text as="div" variant="studio/body/sm">
        {message}
      </Text>
      {type === 'action' && actionText && onAction && (
        <>
          <Button size="sm" className="flex-none">
            {actionText}
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              close(e as any);
            }}
          >
            <CrossIcon className="mx-0.5 h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}

function NoticeIcon({
  type,
  className,
}: {
  type: Notice['type'];
  className: string;
}) {
  if (type === 'error') {
    return (
      <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0.877075 7.49988C0.877075 3.84219 3.84222 0.877045 7.49991 0.877045C11.1576 0.877045 14.1227 3.84219 14.1227 7.49988C14.1227 11.1575 11.1576 14.1227 7.49991 14.1227C3.84222 14.1227 0.877075 11.1575 0.877075 7.49988ZM7.49991 1.82704C4.36689 1.82704 1.82708 4.36686 1.82708 7.49988C1.82708 10.6329 4.36689 13.1727 7.49991 13.1727C10.6329 13.1727 13.1727 10.6329 13.1727 7.49988C13.1727 4.36686 10.6329 1.82704 7.49991 1.82704ZM9.85358 5.14644C10.0488 5.3417 10.0488 5.65829 9.85358 5.85355L8.20713 7.49999L9.85358 9.14644C10.0488 9.3417 10.0488 9.65829 9.85358 9.85355C9.65832 10.0488 9.34173 10.0488 9.14647 9.85355L7.50002 8.2071L5.85358 9.85355C5.65832 10.0488 5.34173 10.0488 5.14647 9.85355C4.95121 9.65829 4.95121 9.3417 5.14647 9.14644L6.79292 7.49999L5.14647 5.85355C4.95121 5.65829 4.95121 5.3417 5.14647 5.14644C5.34173 4.95118 5.65832 4.95118 5.85358 5.14644L7.50002 6.79289L9.14647 5.14644C9.34173 4.95118 9.65832 4.95118 9.85358 5.14644Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        ></path>
      </svg>
    );
  }
  if (type === 'info' || type === 'warning') {
    return (
      <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.36689 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.36689 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        ></path>
      </svg>
    );
  }
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.49991 0.877045C3.84222 0.877045 0.877075 3.84219 0.877075 7.49988C0.877075 11.1575 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1576 0.877045 7.49991 0.877045ZM1.82708 7.49988C1.82708 4.36686 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6329 13.1727 7.49991 13.1727C4.36689 13.1727 1.82708 10.6329 1.82708 7.49988ZM10.1589 5.53774C10.3178 5.31191 10.2636 5.00001 10.0378 4.84109C9.81194 4.68217 9.50004 4.73642 9.34112 4.96225L6.51977 8.97154L5.35681 7.78706C5.16334 7.59002 4.84677 7.58711 4.64973 7.78058C4.45268 7.97404 4.44978 8.29061 4.64325 8.48765L6.22658 10.1003C6.33054 10.2062 6.47617 10.2604 6.62407 10.2483C6.77197 10.2363 6.90686 10.1591 6.99226 10.0377L10.1589 5.53774Z"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      ></path>
    </svg>
  );
}
